import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Loader2, RefreshCw, RotateCw, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

type Row = {
  id: string;
  stripe_event_id: string;
  type: string;
  status: string;
  livemode: boolean;
  error: string | null;
  received_at: string;
  processed_at: string | null;
  last_attempt_at: string | null;
  next_retry_at: string | null;
  retry_count: number;
  alert_sent: boolean;
  related_type: string | null;
  related_id: string | null;
  payload: any;
};

const STATUS_COLORS: Record<string, string> = {
  processed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  received: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  retrying: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  failed: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  permanently_failed: "bg-red-700/30 text-red-200 border-red-700/60",
  ignored: "bg-muted text-muted-foreground border-border",
};

export default function AdminWebhookTimeline() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Row | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("stripe_webhook_events")
      .select("id,stripe_event_id,type,status,livemode,error,received_at,processed_at,last_attempt_at,next_retry_at,retry_count,alert_sent,related_type,related_id,payload")
      .order("received_at", { ascending: false })
      .limit(200);
    if (error) toast.error(error.message);
    setRows((data ?? []) as any);
    setLoading(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (status !== "all" && r.status !== status) return false;
      if (q.trim()) {
        const n = q.toLowerCase();
        if (![r.type, r.stripe_event_id, r.error ?? "", r.related_id ?? ""].some(v => v.toLowerCase().includes(n))) return false;
      }
      return true;
    });
  }, [rows, status, q]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach(r => { c[r.status] = (c[r.status] ?? 0) + 1; });
    return c;
  }, [rows]);

  const retry = async (row: Row) => {
    setRetrying(row.id);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-event-replay", {
        body: { stripe_event_id: row.stripe_event_id },
      });
      if (error) throw error;
      toast.success(`Replay: ${data?.status ?? "ok"}`);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally { setRetrying(null); }
  };

  const runWorker = async () => {
    const { data, error } = await supabase.functions.invoke("stripe-webhook-retry-worker");
    if (error) return toast.error(error.message);
    toast.success(`Worker processed ${data?.processed ?? 0} due event(s)`);
    await load();
  };

  return (
    <AdminShell title="WEBHOOK DELIVERY TIMELINE" actions={
      <div className="flex gap-2">
        <Button variant="outline" onClick={runWorker}><RotateCw className="w-4 h-4 mr-2"/>Run retry worker</Button>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
        </Button>
      </div>
    }>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
        {["processed","received","retrying","failed","permanently_failed","ignored"].map(s => (
          <button key={s} onClick={() => setStatus(s === status ? "all" : s)}
            className={`border p-3 text-left ${status===s ? "border-primary" : "border-border"}`}>
            <div className="text-xs uppercase text-muted-foreground">{s.replace("_"," ")}</div>
            <div className="text-xl font-mono">{counts[s] ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-3">
        <Input placeholder="Search event id, type, error, booking id…" value={q} onChange={e=>setQ(e.target.value)} className="max-w-md"/>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="processed">processed</SelectItem>
            <SelectItem value="received">received</SelectItem>
            <SelectItem value="retrying">retrying</SelectItem>
            <SelectItem value="failed">failed</SelectItem>
            <SelectItem value="permanently_failed">permanently_failed</SelectItem>
            <SelectItem value="ignored">ignored</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border">
        <table className="w-full text-sm">
          <thead className="bg-panel text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left p-2">Received</th>
              <th className="text-left p-2">Type</th>
              <th className="text-left p-2">Event ID</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Attempts</th>
              <th className="text-left p-2">Next retry</th>
              <th className="text-left p-2">Booking</th>
              <th className="text-left p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-t border-border hover:bg-panel/40">
                <td className="p-2 whitespace-nowrap" title={r.received_at}>{formatDistanceToNow(new Date(r.received_at))} ago</td>
                <td className="p-2 font-mono text-xs">{r.type}</td>
                <td className="p-2 font-mono text-xs">{r.stripe_event_id.slice(0,20)}…</td>
                <td className="p-2">
                  <Badge variant="outline" className={STATUS_COLORS[r.status] ?? ""}>
                    {r.status === "permanently_failed" && <AlertTriangle className="w-3 h-3 mr-1"/>}
                    {r.status === "processed" && <CheckCircle2 className="w-3 h-3 mr-1"/>}
                    {r.status}
                  </Badge>
                  {r.alert_sent && <span className="ml-2 text-xs text-amber-400">alerted</span>}
                </td>
                <td className="p-2 font-mono">{r.retry_count}</td>
                <td className="p-2 text-xs">{r.next_retry_at ? formatDistanceToNow(new Date(r.next_retry_at), {addSuffix:true}) : "—"}</td>
                <td className="p-2 text-xs">
                  {r.related_type === "booking" && r.related_id ? (
                    <Link to={`/admin/bookings/${r.related_id}`} className="text-primary hover:underline inline-flex items-center gap-1">
                      {r.related_id.slice(0,8)} <ExternalLink className="w-3 h-3"/>
                    </Link>
                  ) : "—"}
                </td>
                <td className="p-2">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={()=>setSelected(r)}>Inspect</Button>
                    {(r.status === "failed" || r.status === "permanently_failed" || r.status === "retrying") && (
                      <Button size="sm" variant="outline" onClick={()=>retry(r)} disabled={retrying===r.id}>
                        {retrying===r.id ? <Loader2 className="w-3 h-3 animate-spin"/> : "Retry"}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td className="p-6 text-center text-muted-foreground" colSpan={8}>No events match these filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <Sheet open={!!selected} onOpenChange={(v)=>!v && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader><SheetTitle className="font-mono text-sm">{selected?.stripe_event_id}</SheetTitle></SheetHeader>
          {selected && (
            <div className="mt-4 space-y-3 text-sm">
              <div><span className="text-muted-foreground">Type:</span> <code>{selected.type}</code></div>
              <div><span className="text-muted-foreground">Status:</span> {selected.status} · attempts: {selected.retry_count}</div>
              <div><span className="text-muted-foreground">Received:</span> {new Date(selected.received_at).toLocaleString()}</div>
              {selected.last_attempt_at && <div><span className="text-muted-foreground">Last attempt:</span> {new Date(selected.last_attempt_at).toLocaleString()}</div>}
              {selected.processed_at && <div><span className="text-muted-foreground">Processed:</span> {new Date(selected.processed_at).toLocaleString()}</div>}
              {selected.next_retry_at && <div><span className="text-muted-foreground">Next retry:</span> {new Date(selected.next_retry_at).toLocaleString()}</div>}
              {selected.error && (
                <div>
                  <div className="text-muted-foreground mb-1">Error</div>
                  <pre className="bg-panel border border-border p-2 text-xs overflow-x-auto whitespace-pre-wrap">{selected.error}</pre>
                </div>
              )}
              <div>
                <div className="text-muted-foreground mb-1">Payload</div>
                <pre className="bg-panel border border-border p-2 text-xs overflow-x-auto">{JSON.stringify(selected.payload, null, 2)}</pre>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AdminShell>
  );
}